import { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import api from '../../api/axios';

interface SourceSummary {
  source_id: string;
  display_name: string;
  type: string;
  authority: string;
  status: string;
  release_date: string | null;
  citation_count: number;
  updated_at: string;
}

interface Props {
  selectedId?: string;
  onSelect: (id: string) => void;
}

const StatuteList = ({ selectedId, onSelect }: Props) => {
  const [sources, setSources] = useState<SourceSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api
      .get('/explore/sources/', { params: { types: 'statute,regulation', limit: 200 } })
      .then((res) => { if (!cancelled) setSources(res.data.sources || []); })
      .catch((err) => {
        if (!cancelled) setError(err?.response?.data?.error || err.message || 'failed');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = sources.filter((s) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      s.source_id.toLowerCase().includes(q) ||
      (s.display_name || '').toLowerCase().includes(q) ||
      (s.authority || '').toLowerCase().includes(q)
    );
  });

  return (
    <Box>
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', backgroundColor: 'background.paper' }}>
        <Typography variant="overline" color="text.secondary">Statutes</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          {loading ? 'loading…' : filtered.length}
        </Typography>
        <Box sx={{ mt: 1 }}>
          <TextField
            size="small"
            fullWidth
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Box>

      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress size={24} /></Box>}
      {error && <Box sx={{ p: 2 }}><Typography color="error" variant="body2">{error}</Typography></Box>}
      {!loading && !error && filtered.length === 0 && (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No statutes registered yet.</Typography>
        </Box>
      )}
      {filtered.map((s) => {
        const active = s.source_id === selectedId;
        return (
          <Box
            key={s.source_id}
            onClick={() => onSelect(s.source_id)}
            sx={{
              px: 2, py: 1.5,
              borderBottom: 1, borderColor: 'divider',
              cursor: 'pointer',
              backgroundColor: active ? 'rgba(58, 68, 82, 0.08)' : 'transparent',
              borderLeft: active ? '3px solid' : '3px solid transparent',
              borderLeftColor: active ? 'primary.main' : 'transparent',
              '&:hover': { backgroundColor: active ? 'rgba(58, 68, 82, 0.12)' : 'rgba(0,0,0,0.03)' },
              transition: 'background-color 0.12s',
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.3 }}>
              {s.display_name || s.source_id}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
              <Chip label={s.type} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
              {s.authority && (
                <Chip label={s.authority} size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
              )}
              <Chip
                label={`${s.citation_count} cite${s.citation_count === 1 ? '' : 's'}`}
                size="small"
                sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(0,0,0,0.06)' }}
              />
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default StatuteList;
